import React, { forwardRef, useImperativeHandle, useState, Ref } from "react";
import useDidMountEffect from "modules/utils/hooks/useDidMountEffect";
import { useMediaQuery } from "@material-ui/core";
import FileAlreadyUploaded from "../presentation/FileAlreadyUploaded";
import UploadFile, {
    FileProps,
    VideoConstraintsProps,
} from "../presentation/UploadFile";
import useStyles from "../presentation/useStyles";
import { useEffect } from "@storybook/client-api";
import { IdhaDialogs, useDialog } from "@idha/sanitization-components";
import { useTranslation } from "react-i18next";

export interface UploadFileManagerProps {
    // Função que atualiza o tamanho da SwipeableView
    updateSwipeableView?: () => void;
    // Callback chamado quando o usuário seleciona um arquivo para fazer uplaod
    handleOnChangeSelectedFile: (file: FileProps) => void;
    // Callback chamado quando o usuário faz o upload do arquivo
    handleOnUploadFile: () => Promise<void>;

    // Callback chamado quando o usuário salvar o formulário após ter selecionado opção de remover o arquivo que foi feito o upload
    handleOnRemoveUploadedFile: () => Promise<void>;

    videoConstraints?: VideoConstraintsProps;

    // Titulo que aparece quando existe uma imagem carregada
    uploadTitle?: string;

    // Arquivo selecionado
    selectedFile?: FileProps;

    // Arquivo selecionado
    uploadedFileURL?: string;

    ref?: Ref<RefProps>;
}
type StatusFileManagerOptions =
    | "removingFile"
    | "updatingFile"
    | "notChangedFile";

export interface RefProps {
    getUploadActionOnSubmit: () => StatusFileManagerOptions;
}

const UploadFileManager = forwardRef<RefProps, UploadFileManagerProps>(
    (
        {
            updateSwipeableView,
            handleOnChangeSelectedFile,
            handleOnUploadFile,
            handleOnRemoveUploadedFile,
            selectedFile,
            uploadedFileURL,
            videoConstraints = {
                width: "320px",
                height: "200px",
                facingMode: "user",
            },
            uploadTitle,
        },
        forwardedRef
    ) => {
        const { t } = useTranslation(["FileUploadv2"]);

        const [cameraRecording, setCameraRecording] = useState(false);
        const [
            uploadFileManagerStatus,
            setUploadFileManagerStatus,
        ] = useState<StatusFileManagerOptions>("notChangedFile");

        const { uploadContainer } = useStyles();

        const [
            changeCurrentUploadedFile,
            setChangeCurrentUploadedFile,
        ] = useState(false);
        const cellphoneScreen = useMediaQuery("(max-width:460px)");
        useDidMountEffect(() => {
            updateSwipeableView && updateSwipeableView();
        }, [
            cellphoneScreen,
            selectedFile,
            cameraRecording,
            uploadFileManagerStatus,
        ]);

        const [dlgProps, dialog] = useDialog();

        const handleOpenCamera = () => {
            setCameraRecording(true);
        };
        const handleCloseCamera = () => {
            setCameraRecording(false);
        };
        const iternalHandleOnUploadFile = async () => {
            await handleOnUploadFile();
            setChangeCurrentUploadedFile(false);
            setUploadFileManagerStatus("updatingFile");
        };

        const handleRequestToDeleteCurrentFile = () => {
            setUploadFileManagerStatus("removingFile");
        };
        const handleChangeCurrentUploadedFile = () => {
            setChangeCurrentUploadedFile(true);
        };

        const getUploadActionOnSubmit = () => {
            return uploadFileManagerStatus;
        };

        useImperativeHandle(forwardedRef, () => ({
            getUploadActionOnSubmit,
        }));

        const validateFile = (file: FileProps) => {

            const typeFile = file?.file.type.split('/')[1]            
            if(typeFile){
                if(!['png', 'jpeg', 'jpg'].find(f => f == typeFile)){
                    dialog
                    .confirm('')
                    .withMessage(t("bad_content_dialog.file_invalid_type", {
                        fileformat: "png ou jpeg",
                    }))
                    .withConfirmLabel('OK')
                    .onConfirm(() => handleOnChangeSelectedFile(undefined))
                    .show()
                } else if (file?.file.size/2e6 > 0.999999) {
                    dialog
                    .confirm('')
                    .withMessage(t("bad_content_dialog.file_to_large", {
                        maxSize: "2MB",
                    }))
                    .withConfirmLabel('OK')
                    .onConfirm(() => handleOnChangeSelectedFile(undefined))
                    .show()
                }else{
                    handleOnChangeSelectedFile(file)
                }
            } else{
                handleOnChangeSelectedFile(undefined)
            }
        }


        return (
            <div className={uploadContainer}>
                {uploadedFileURL && !changeCurrentUploadedFile ? (
                    <FileAlreadyUploaded
                        {...{
                            uploadTitle,
                            uploadFileManagerStatus,
                            handleRequestToDeleteCurrentFile,
                            handleChangeCurrentUploadedFile,
                            cellphoneScreen,
                        }}
                        imgWidth={videoConstraints.width}
                        imgHeight={videoConstraints.height}
                        previewFileUrl={uploadedFileURL}
                    />
                ) : (
                    <UploadFile
                        {...{
                            handleOpenCamera,
                            handleCloseCamera,
                            selectedFile,
                            handleOnUploadFile: iternalHandleOnUploadFile,
                            handleOnChangeSelectedFile: validateFile,
                            cameraRecording,
                            videoConstraints,
                        }}
                    />
                )}
            <IdhaDialogs {...dlgProps} />
            </div>
        );
    }
);
export default UploadFileManager;
